import useRecursiveTimeout from "@insite/client-framework/Common/Hooks/useRecursiveTimeout";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeMenuStyles } from "@insite/content-library/Components/NavigationModeMenu";
import { Hidden, Link, Typography } from "@insite/mobius/index";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "@adobe/css-tools";
import useEmblaCarousel from "embla-carousel-react";
import React, { FC, useCallback } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import parse from "html-react-parser";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";

const enum fields {
    customCSS = "customCSS",
    sections = "sections",
    color = "color",
    paddingTop = "paddingTop",
    paddingBottom = "paddingBottom",
    paddingRight = "paddingRight",
    paddingLeft = "paddingLeft",
}

interface SectionModel {
    fields: {
        heading: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sections]: SectionModel[];
        [fields.customCSS]: string;
        [fields.color]: string;
        [fields.paddingTop]: string;
        [fields.paddingBottom]: string;
        [fields.paddingRight]: string;
        [fields.paddingLeft]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    enableWarehousePickup: getSettingsCollection(state).accountSettings.enableWarehousePickup,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const Container = styled.div`
    overflow: hidden;
    .embla__container {
        display: flex;
        height: 28px;
    }
    .embla__slide {
        flex: 0 0 100%;
        min-width: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .flexslider-slides-text {
        color: #ffffff;
        font-size: 13px;
        margin: 0;
        &.top-contact {
            margin-left: 4px;
        }
        strong {
            color: #ffffff;
        }
        span {
            margin: 0;
            font-size: 13px;
        }
        p {
            margin: 0;
            font-size: 13px;
        }
    }
`;

export interface SecondaryNavigationStyles {
    wrapper?: InjectableCss;
    navigationModeMenu?: NavigationModeMenuStyles;
    languageLink?: InjectableCss;
    warehousePickupLink?: InjectableCss;
    signInLink?: InjectableCss;
}

export const secondaryNavigationStyles: SecondaryNavigationStyles = {
    languageLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
        `,
    },
    warehousePickupLink: {
        css: css`
            flex: 0 1 auto;
            margin-left: 30px;
            min-width: 0;
        `,
    },
    signInLink: {
        css: css`
            flex: 0 0 auto;
            margin-left: 30px;
        `,
    },
};

const SecondaryNavigationBanner: FC<Props> = ({ id, enableWarehousePickup, fields }) => {
    const styles = useMergeStyles("secondaryNavigation", secondaryNavigationStyles);

    const [emblaRef, embla] = useEmblaCarousel({ loop: true });

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }
        embla.scrollNext();
    }, [embla]);

    useRecursiveTimeout(scrollNext, 8000);

    return (
        <Container
            className="embla"
            ref={emblaRef}
            style={{
                backgroundColor: fields.color,
                paddingTop: `${fields.paddingTop}px`,
                paddingBottom: `${fields.paddingBottom}px`,
                paddingRight: `${fields.paddingRight}px`,
                paddingLeft: `${fields.paddingLeft}px`,
            }}
        >
            <StyledWrapper className="embla__container">
                {fields.sections.map((section, index) => (
                    <StyledWrapper key={index} className="embla__slide">
                        <StyledWrapper>
                            <Typography className="flexslider-slides-text">
                                {parse(section.fields.heading, parserOptions)}
                            </Typography>
                        </StyledWrapper>
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        </Container>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const secondaryNavigationBanner: WidgetModule = {
    component: connect(mapStateToProps)(SecondaryNavigationBanner),
    definition: {
        group: "Common",
        icon: "link-simple",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.color,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "black",
                // isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            {
                name: fields.sections,
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                getDisplay: (item: HasFields) => {
                    return item.fields.description;
                },
                fieldDefinitions: [
                    {
                        name: "heading",
                        editorTemplate: "RichTextField",
                        displayName: "Heading",
                        defaultValue: "",
                    },
                ],
            },
            {
                fieldType: "General",
                name: fields.paddingTop,
                editorTemplate: "TextField",
                displayName: "Padding Top",
                defaultValue: "0",
                // isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.paddingBottom,
                editorTemplate: "TextField",
                displayName: "Padding Bottom",
                defaultValue: "0",
                // isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.paddingRight,
                editorTemplate: "TextField",
                displayName: "Padding Right",
                defaultValue: "0",
                // isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.paddingLeft,
                editorTemplate: "TextField",
                displayName: "Padding Left",
                defaultValue: "0",
                // isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
        ],
    },
};

export default secondaryNavigationBanner;
